import React from 'react';

export const employmentLabel = (status) => ({
  current: 'Current employee', former: 'Former employee', planned: 'Future employee',
  none: 'No employment dates',
}[status] || 'No employment dates');

export const employmentSyncLabel = (status) => ({
  pending: 'Saved in Tempus · CRM confirmation pending',
  not_configured: 'Saved in Tempus · CRM synchronization unavailable',
  conflict: 'CRM conflict · correction not confirmed',
  error: 'CRM synchronization failed',
  synced: 'Confirmed in CRM',
  scheduled: 'Scheduled employment correction',
  cancelled: 'Cancelled in CRM · review employment dates',
  superseded: 'Replaced in CRM · review employment dates',
  review_required: 'CRM employment differs · review employment dates',
}[status] || '');

export const employmentErrorLabel = (code) => ({
  CRM_NOT_CONFIGURED: 'Configure the CRM employment integration, then reopen employment.',
  CRM_REVISION_REQUIRED: 'Load the current CRM employment details before saving a new decision.',
  CRM_REVISION_CONFLICT: 'CRM employment changed. Reopen employment and compare the current dates.',
  CRM_IDENTITY_NOT_LINKED: 'The employee, customer or placement is not linked to CRM. Review its integration links.',
  CRM_IDENTITY_CONFLICT: 'CRM returned a different employee, customer or placement. Resolve the identity conflict.',
  CRM_EMPLOYEE_PROTECTED: 'CRM protects this employee from employment changes. Resolve the restriction in CRM.',
  CRM_COMPANY_PROTECTED: 'CRM protects this customer from employment changes. Resolve the restriction in CRM.',
  CRM_CONTENT_MISMATCH: 'The existing command differs from the CRM record. Review the original decision.',
  CRM_INVALID_DATES: 'CRM rejected the employment dates. Review the interval.',
  CRM_PLACEMENT_OVERLAP: 'CRM has an overlapping employment interval. Compare the placements.',
  CRM_TARGET_NOT_FOUND: 'CRM could not find the linked employee or placement. Review its integration links.',
  CRM_EMPLOYEE_NOT_FOUND: 'CRM could not find the linked employee. Review the employee integration link.',
  CRM_PLACEMENT_NOT_FOUND: 'CRM could not find the linked placement. Review the placement integration link.',
  CRM_COMMAND_CONTENT_MISMATCH: 'CRM already has different content for this decision. Review the original decision before proceeding.',
  CRM_COMMAND_ID_MISMATCH: 'CRM could not verify this decision identity. Resolve the conflict before proceeding.',
  CRM_INVALID_COMMAND: 'CRM rejected the decision format. Review the integration and employment details.',
  CRM_COMMAND_SUPERSESSION_REQUIRED: 'CRM has a newer decision. Refresh employment facts and compare the latest decision.',
  CRM_STATE_VERSION_REQUIRED: 'CRM requires the updated employment integration. Coordinate the application releases.',
  CRM_INVALID_QUERY: 'CRM could not read this employment request. Review the integration setup.',
  CRM_SCOPE_REQUIRED: 'The CRM credential does not permit this action. Review the integration permissions.',
  CRM_ENDPOINT_UNAVAILABLE: 'The required CRM endpoint is unavailable. Check the CRM integration setup.',
  CRM_SCHEMA_UNSUPPORTED: 'CRM does not support the updated employment integration. Coordinate the CRM release.',
  CRM_DEPENDENCY_UNAVAILABLE: 'A CRM service is temporarily unavailable. Retry the original decision after service is restored.',
  CRM_UNREACHABLE: 'CRM could not be reached. Retry the original decision after service is restored.',
  CRM_BAD_RESPONSE: 'CRM returned employment details that could not be verified. Check the integration before retrying.',
  CRM_BAD_RECEIPT: 'CRM confirmation could not be verified. Recover the original decision before replacing it.',
  CRM_VALIDATION_CONFLICT: 'CRM rejected the employment decision. Compare the current employment details.',
  CRM_EMPLOYMENT_DIVERGED: 'The current CRM dates differ from Tempus. Compare both systems before saving a correction.',
  CRM_COMMAND_CANCELLED: 'CRM cancelled this decision. Local employment dates remain available for review.',
  CRM_COMMAND_SUPERSEDED: 'CRM replaced this decision. Local employment dates remain available for review.',
}[code] || (code ? 'Open employment to review the synchronization details.' : ''));

export default function EmploymentStatus({ employee, dates = true }) {
  const syncLabel = employmentSyncLabel(employee.employmentSyncStatus);
  return (
    <div style={{ minWidth: 165, whiteSpace: 'normal' }}>
      <strong style={{ color: employee.employmentStatus === 'current' ? '#1e8449' : '#596779' }}>
        {employmentLabel(employee.employmentStatus)}
      </strong>
      {dates && <div style={{ fontSize: '0.78rem', marginTop: 4 }}>
        {employee.employmentStartDate || '—'} – {employee.employmentEndDate || (employee.employmentStartDate ? 'Open ended' : '—')}
      </div>}
      {employee.employmentCustomerName && <div style={{ fontSize: '0.78rem' }}>{employee.employmentCustomerName}</div>}
      {syncLabel && <div role={['conflict', 'error', 'cancelled', 'superseded', 'review_required'].includes(employee.employmentSyncStatus) ? 'alert' : undefined}
        style={{ fontSize: '0.75rem', marginTop: 4, color: employee.employmentSyncStatus === 'synced' ? '#1e8449' : '#9a6a10' }}>
        {syncLabel}{employee.employmentSyncError ? `: ${employmentErrorLabel(employee.employmentSyncError)}` : ''}
      </div>}
    </div>
  );
}
